import CommonTips, { ICommonTipsConfig } from '@src/framework/frame/components/common-tips';
import ENV from '@src/slide/infrastructure/module/dependent/runtime-environment/enviroment';
import { getCookieUid } from '@tencent/tencent-doc-report';
import { getResourceMapKeyframe } from '@tencent/slide-sdk-model/src/keyframe/get-resource-map-keyframe';
import { getSlideFileKeyframe } from '@tencent/slide-sdk-model/src/keyframe/get-slide-file-keyframe';
import { IdGenerator } from '../../../sync/common/util/id-generator';
import { initInMainThead } from '../../../sync/main-thread/init-main-thead';
import { isClientDriveFile } from '@src/slide/infrastructure/module/dependent/runtime-environment/drive-env';
import {
    getRandomNumber,
    IPresentationData,
    IValidMutationData,
    SlideFile,
} from '@tencent/slide-sdk-model';
import type { IActionProvider } from '../../../sync/common/interface/services.interface';
import type { ICreateConfig } from '../../../sync/worker/main/sync-manager';
import type { IDocInfoManager } from '../../interfaces/doc-info.interface';
import type { ILogger } from '../../slide-data-services.core';
import type { IPresentationStoreService } from '../../interfaces/presentation-service.interface';
import { MonitorStatus } from '@tencent/slide-sdk-model/src/manager/logger.interface';

export interface ISyncManager {
    isReady: boolean;
    createDoc(): Promise<void>;
    syncDoc(): Promise<void>;
}

export abstract class SyncManager implements ISyncManager {
    protected tipsId = 'sync-progress-tips';
    protected workerService: ReturnType<typeof initInMainThead>;
    protected sessionId = getRandomNumber().toString();
    protected actionService: IActionProvider;
    protected ready = false;

    public constructor(
        protected docInfoManager: IDocInfoManager,
        protected presentationStoreService: IPresentationStoreService,
        protected logger: ILogger,
    ) {}

    public get isReady() {
        return this.ready;
    }

    public get docInfoService() {
        return {
            getDocInfo: () => {
                const { globalPadId = '', userId = '', cGlobalPadId = '' } = this.docInfoManager;
                return {
                    globalPadId,
                    cGlobalPadId,
                    userId,
                    isDriveDoc: isClientDriveFile(),
                    isLocalDoc: ENV.IS_LOCAL_EDIT,
                    sessionId: this.sessionId,
                    uid: getCookieUid() ?? '',
                };
            },
        };
    }

    public showTips(config: ICommonTipsConfig) {
        CommonTips.getInstance().show({
            id: this.tipsId,
            block: false,
            style: { boxShadow: 'unset', animation: 'none', transition: 'none' },
            ...config,
        });
    }

    public hideTips() {
        CommonTips.getInstance().hideById(this.tipsId);
    }

    protected async init() {
        try {
            let sharedWorker;

            if (__IS_DEV__) {
                const SharedWorker = (
                    (await import('@src/slide/domain/slide-app/sync/worker/main/local.sharedWorker')) as any
                ).default;
                sharedWorker = new SharedWorker();
            } else {
                sharedWorker = new SharedWorker('https://docs.qq.com/slide/worker/local.sharedWorker.js');
            }

            this.workerService = initInMainThead({
                sharedWorker,
                docInfoProvider: this.docInfoService,
                actionProvider: this.actionService,
            });
        } catch (err) {
            this.logger.monitor?.reportStatus?.('LFT-init', MonitorStatus.ERROR, err?.toString(), [this.docInfoManager.userId ?? '']);
        }
    }

    protected getUid() {
        return getCookieUid() ?? '';
    }

    /**
     * 获取创建配置的异步方法
     *
     * @returns {Promise<ICreateConfig>} 返回一个包含创建配置的 Promise 对象
     *
     * @description
     * 该方法准备关键帧和数据块，并生成一个创建配置对象。配置对象包括标题、关键帧数据、数据块信息等。
     *
     * 具体步骤：
     * 1. 调用 `prepareKeyframe` 方法获取关键帧和数据块。
     * 2. 生成一个不需要通知的任务 ID。
     * 3. 从文档信息管理器中获取全局 Pad ID 和标题。
     * 4. 获取用户 ID。
     * 5. 处理每个数据块，生成相应的配置对象。
     * 6. 返回包含所有相关信息的配置对象。
     */
    protected async getCreateConfig(): Promise<ICreateConfig> {
        const { presentationKf, chunkData } = await this.prepareKeyframe();
        let taskId = IdGenerator.getUnNoticeTaskId();
        const { globalPadId = '', title = '' } = this.docInfoManager;
        const uid = this.getUid();
        const subId = 'BB08J2';
        return {
            title,
            keyframe: JSON.stringify({ ...presentationKf, dver: '3.0.0' }),
            chunkData: chunkData.map((mDatas) => {
                taskId += 1;
                return {
                    globalPadId: '',
                    version: 0,
                    uid,
                    subId,
                    taskId,
                    data: JSON.stringify({
                        mutations: mDatas,
                        taskId,
                        revision: 0,
                    }),
                };
            }),
            globalPadId,
            uid,
            subId,
            sessionId: this.sessionId,
        };
    }

    /**
     * 准备关键帧数据的异步方法
     *
     * @returns {Promise<{ presentationKf: any, chunkData: IValidMutationData[][] }>}
     * 返回一个包含关键帧数据和数据块的 Promise 对象
     *
     * @description
     * 该方法从演示文稿中获取数据，处理资源映射，并生成关键帧和数据块。
     *
     * 具体步骤：
     * 1. 从演示文稿存储服务中获取拥有的数据，并将其合并到一个新的对象中。
     * 2. 通过 `getResourceMapKeyframe` 函数获取资源映射的关键帧数据。
     * 3. 初始化资源映射对象，包含图像、视频、音频和字体。
     * 4. 获取幻灯片文件的关键帧数据。
     * 5. 创建一个新的幻灯片文件关键帧对象，并将页面数据添加到其中。
     * 6. 如果幻灯片文件的关键帧中存在页面数据，则确保在创建的对象中包含第一个幻灯片的页面数据。
     * 7. 使用 `SlideFile.fromSlideFileKeyframe` 方法将创建的幻灯片文件关键帧转换为数据格式。
     * 8. 生成数据块，包含所有页面和资源数据。
     * 9. 返回包含关键帧和数据块的对象。
     */
    protected async prepareKeyframe() {
        const data = Object.assign(
            {},
            this.presentationStoreService.getPresentation().getOwnedData(),
        ) as IPresentationData;

        const resourceData = getResourceMapKeyframe(data.resourceMap);
        data.resourceMap = {
            images: {},
            videos: {},
            audios: {},
            fonts: {},
        };

        const slideFileKeyframe = getSlideFileKeyframe(data);
        const createSlideFileKeyframe = {
            presentation: slideFileKeyframe.presentation,
            pages: {},
        };

        if (slideFileKeyframe.pages) {
            const firstSlideId = this.presentationStoreService.getPresentation().slideList[0].id;
            Object.entries(slideFileKeyframe.pages).forEach(([slideId, muts]) => {
                if (slideId === firstSlideId) {
                    createSlideFileKeyframe.pages[slideId] = muts;
                    return;
                }
                createSlideFileKeyframe.pages[slideId] = [muts.shift()];
            });
            Reflect.deleteProperty(slideFileKeyframe.pages, firstSlideId);
        }

        const presentationKf = SlideFile.fromSlideFileKeyframe(createSlideFileKeyframe).getData();
        const chunkData: IValidMutationData[][] = [
            ...Object.values(slideFileKeyframe.pages),
            ...resourceData.map(mData => [mData]),
        ];

        return { presentationKf, chunkData };
    }

    protected registerEvent() {
        window.addEventListener('beforeunload', () => {
            this.workerService?.portClientManager.removePortClient(
                this.docInfoManager.globalPadId ?? '',
                this.sessionId,
            );
        });
    }

    public abstract createDoc(): Promise<void>;
    public abstract syncDoc(): Promise<void>;
}
